import ReactDataGrid from '@inovua/reactdatagrid-community';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  Grid,
  Group,
  Loader,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconArrowRight,
  IconCaretLeftFilled,
  IconCaretRightFilled,
  IconCircleCheck,
  IconCloudUpload,
  IconInfoCircle,
  IconMailFast,
  IconMessages,
  IconMinus,
  IconPlus,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
// import 'react-data-grid/lib/styles.css';
import '@inovua/reactdatagrid-community/index.css';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import 'react-toastify/dist/ReactToastify.css';
import InvoiceApproverAdapter from '../adapters/invoiceApproversAdapter.js';
import TransactionAdapter from '../adapters/transactionAdapter.js';
import DocHistoryDropdown from '../components/DocHistoryDropdown';
import GRVLines from '../components/GRVLines.jsx';
import LoadingScreen from '../components/LoadingScreen';
import PDFViewerNative from '../components/PDFViewerNative.jsx';
import PurchaseOrderLines from '../components/PurchaseOrderLines';
import SelectApproverItem from '../components/SelectApproverItem';
import docStatusMap from '../constants/docStatusMap.js';
import docStatusNameMap from '../constants/docStatusNameMap.js';
import useFetchDocument from '../hooks/useFetchDocument.jsx';
import usePurchaseOrderDetails from '../hooks/usePurchaseOrderDetails.jsx';
import formatCurrency from '../utils/formatCurrency.js';
import {
  getAdjacentDocumentIds,
  removeDocumentFromNavigationContext,
} from '../utils/documentNavigationContext.js';

const hideDocHistoryFrom = [
  docStatusMap.zeroQ,
  docStatusMap.completeQ,
  docStatusMap.squashQ,
];

// List of views where Squash button is expected to be visible.
const squashViews = [
  docStatusMap.manualQ,
  docStatusMap.queryQ,
  docStatusMap.poNoMatchQ,
  docStatusMap.noGrvQ,
  docStatusMap.serviceInvoiceQ,
  docStatusMap.grvInvoiceQ,
  docStatusMap.rejectedQ,
  docStatusMap.duplicateQ,
  docStatusMap.sapQueryQ,
];

// List of views where Push Back button is expected to be visible.
const pushBackViews = [
  docStatusMap.batchQ,
  docStatusMap.sapWaitingQ,
  docStatusMap.duplicateQ,
  docStatusMap.sapQueryQ,
  docStatusMap.squashQ,
];

// List of views where detailed invoice view will allow
// the users to update the form values...
const editableViews = [
  docStatusMap.manualQ,
  docStatusMap.poNoMatchQ,
  docStatusMap.queryQ,
  docStatusMap.serviceInvoiceQ,
  docStatusMap.grvInvoiceQ,
  docStatusMap.noGrvQ,
  docStatusMap.rejectedQ,
];

const expenseColumn = [
  {
    name: 'Code',
    header: 'Code',
    defaultFlex: 2,
    defaultVisible: false,
    sortable: false,
  },
  {
    name: 'Description',
    header: 'Description',
    defaultFlex: 4,
    sortable: false,
  },
  {
    name: 'Quantity',
    header: 'Quantity',
    render: ({ value }) => Number(value).toFixed(2),
    defaultFlex: 1,
    headerAlign: 'center',
    textAlign: 'end',
    sortable: false,
  },
  {
    name: 'UnitPrice',
    header: 'Unit Price',
    render: ({ value }) => formatCurrency(value),
    defaultFlex: 1,
    textAlign: 'end',
    headerAlign: 'center',
    sortable: false,
  },
  {
    name: 'Discount',
    header: 'Discount',
    defaultFlex: 1,
    textAlign: 'center',
    sortable: false,
  },
  {
    name: 'LineTotal',
    header: 'Line Total',
    render: ({ value }) => formatCurrency(value),
    defaultFlex: 1,
    textAlign: 'end',
    headerAlign: 'center',
    sortable: false,
  },
];

const formFieldDescriptions = {
  PO_Number: 'Example: 597861, 486433, 335417',
  Invoice_Date: 'YYYY-MM-DD',
  Invoice_Received_Date: 'YYYY-MM-DD',
};

function DocumentDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [document, error, isLoading, invalidateDocumentCache, refetchDocument] =
    useFetchDocument(id);
  const [localDocument, setLocalDocument] = useState(document);

  const [templateObject, setTemplateObject] = useState({});
  const [botObject, setBotObject] = useState({});
  const [expenseRows, setExpenseRows] = useState({});
  const [botExpenseRows, setBotExpenseRows] = useState({});
  const [lastClickedRow, setLastClickedRow] = useState(0);
  const [isViewSwitched, setIsViewSwitched] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [approverUpdationInProgress, setApproverUpdationInProgress] =
    useState(false);
  const [originalPoNumber, setOriginalPoNumber] = useState(null);
  const [purchaseOrder, purchaseOrderError, purchaseOrderLoading] =
    usePurchaseOrderDetails(originalPoNumber);
  const [poLines, setPoLines] = useState([]);
  const [documentKey, setDocumentKey] = useState(null);
  const [docSignedUrl, setDocSignedUrl] = useState(null);
  const [docSignedUrlLoading, setDocSignedUrlLoading] = useState(false);
  const [docSignedUrlError, setDocSignedUrlError] = useState(null);
  const [processedTotal, setProcessedTotal] = useState(0);
  const [grvReceivedTotal, setGrvReceivedTotal] = useState(0);
  const [
    isNotesDrawerOpen,
    { open: openNotesDrawer, close: closeNotesDrawer },
  ] = useDisclosure(false);
  const [grvLines, setGrvLines] = useState([]);
  const [activeTab, setActiveTab] = useState('invoiceLines');
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const nextButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const backButtonRef = useRef(null);
  const [navContextVersion, setNavContextVersion] = useState(0);

  const navigationTargets = useMemo(() => {
    const clientNav = getAdjacentDocumentIds(id);

    if (clientNav) {
      return {
        prev: clientNav.prev ? { _id: clientNav.prev } : null,
        next: clientNav.next ? { _id: clientNav.next } : null,
      };
    }

    return {
      prev: document?._id === id ? document?.meta?.prev || null : null,
      next: document?._id === id ? document?.meta?.next || null : null,
    };
  }, [id, document?.meta, navContextVersion]);

  const handleDocumentRemovedFromQueue = (documentId) => {
    removeDocumentFromNavigationContext(documentId);
    setNavContextVersion((version) => version + 1);
  };

  const switchView = (fileURL) => {
    setIsViewSwitched(!isViewSwitched);
    if (fileURL !== null) {
      window.open(fileURL, '_blank');
    }
  };

  const gridStyle = { minHeight: 300 };

  const noteForm = useForm({
    initialValues: {
      content: '',
    },
    validate: {
      content: (value) =>
        value.trim().length === 0 ? 'Note cannot be empty' : null,
    },
  });

  useHotkeys([
    [
      'n',
      () => {
        openNotesDrawer();
      },
    ],
    [
      'ArrowRight',
      () => {
        nextButtonRef.current?.focus();
        nextButtonRef.current?.click();
      },
    ],
    [
      'ArrowLeft',
      () => {
        prevButtonRef.current?.focus();
        prevButtonRef.current?.click();
      },
    ],
    [
      'Backspace',
      () => {
        backButtonRef.current?.focus();
        backButtonRef.current?.click();
      },
    ],
  ]);

  const onRowClick = useCallback((rowProps) => {
    setLastClickedRow(rowProps.rowIndex);
  }, []);

  useEffect(() => {
    const invoiceTotal = localDocument?.Document?.totals?.total || 0;
    setInvoiceTotal(invoiceTotal);

    let poLines = localDocument?.Document?.po_line_items;
    if (poLines) {
      if (poLines[0] != undefined) {
        if (!(poLines[0].LineNum != undefined)) {
          poLines = poLines.map((obj, index) => ({ ...obj, LineNum: index }));
        }
        var receivedTotal = 0;
        for (let i = 0; i < poLines.length; i++) {
          receivedTotal += parseFloat(
            poLines[i]?.ReceivedTotal ? poLines[i].ReceivedTotal : '0',
          );
        }
        setProcessedTotal(receivedTotal);
      }
    }
    setPoLines(poLines);

    const grvLines = localDocument?.Document?.gr_line_items || [];
    setGrvLines(grvLines);

    const grvReceivedTotal = grvLines.reduce(
      (acc, line) =>
        acc + parseFloat(line.LineStatus === 'O' ? line.ReceivedLineTotal : 0),
      0,
    );
    setGrvReceivedTotal(grvReceivedTotal);

    if (localDocument?.Document?.gr_line_items?.length > 0) {
      setActiveTab('grvLines');
    } else if (localDocument?.Document?.po_line_items?.length > 0) {
      setActiveTab('poLines');
    } else {
      setActiveTab('invoiceLines');
    }
  }, [localDocument]);

  useEffect(() => {
    const getApproversList = async () => {
      const approvers = await InvoiceApproverAdapter.getInvoiceAprovers();

      setApprovers(
        approvers.map((approver) => ({
          value: approver.email,
          name: approver.name,
          email: approver.email,
          limit: approver.approvalLimit,
          department: approver.department,
          label: `${approver.name} (${approver.email})`,
        })),
      );
    };

    getApproversList();
  }, []);

  useEffect(() => {
    if (document) {
      setLocalDocument(document);
      setOriginalPoNumber(document?.Document?.po_number);
      setDocumentKey(document?.file_name);

      const botObject = getBotObject(document);
      setBotObject(botObject);

      const templateObj = {
        matchedTemplateId: '',
        extraFields: {},
        botObject: { ...botObject },
        expenseRows: [],
        expenseRowsNew: [],
      };
      itemForm.setValues(templateObj); // New

      let rows = [];
      for (
        let index = 0;
        index < templateObj.botObject.lineItemData.length;
        index++
      ) {
        const element = templateObj.botObject.lineItemData[index];
        let rowItem = element;
        rowItem.id = index;
        rows.push(rowItem);
      }

      setExpenseRows(rows);
      setTemplateObject(templateObj);
    }
  }, [document]);

  // Get the signed URL for the document.
  useEffect(() => {
    if (documentKey) {
      // Reset the states before fetching the new signed URL.
      setDocSignedUrl(null);
      setDocSignedUrlError(null);
      setDocSignedUrlLoading(true);

      TransactionAdapter.getSignedUrl(documentKey)
        .then((signedURL) => {
          setDocSignedUrlError(null);
          setDocSignedUrl(signedURL);
        })
        .catch((error) => {
          console.error('Error in getting signed URL: ', error);
          setDocSignedUrlError(error);
        })
        .finally(() => {
          setDocSignedUrlLoading(false);
        });
    }
  }, [documentKey]);

  useEffect(() => {
    console.log('PO Load....');
    if (!purchaseOrder) return;
    console.log('PO Load completed...');
    const docType = purchaseOrder?.PurchaseOrders?.DocType;
    console.log('Doc Type: ', docType);

    const mergedPoLineItems = (purchaseOrder?.PurchaseOrders?.Lines || []).map(
      (incomingItem) => {
        const existingItem = localDocument?.Document?.po_line_items?.find(
          (item) =>
            (item.LineNumber === incomingItem.LineNumber &&
              item.PONumber === incomingItem.PONumber) ||
            (item.LineNum === incomingItem.LineNumber &&
              item.PONumber === incomingItem.PONumber),
        );
        if (existingItem) {
          // Merge the fields from the existing item to preserve fields like "Matches".
          console.log(
            'Line merge::::',
            incomingItem.LineNumber,
            existingItem,
            incomingItem,
          );
          return {
            ...existingItem,
            ...incomingItem,
            VatGroup: existingItem.VatGroup,
            ReceivedTotal: existingItem.ReceivedTotal,
          };
        }
        // If no matching item, add the new item as is
        return incomingItem;
      },
    );
    setPoLines(mergedPoLineItems);
    console.log(
      'Good Receipts UI display:',
      purchaseOrder?.PurchaseOrders?.GoodsReceipts,
    );
    const grvData = purchaseOrder?.PurchaseOrders?.GoodsReceipts || {};
    const grvNumbers = Object.keys(grvData);
    const grvLines = [];
    for (const grvNumber of grvNumbers) {
      const grv = grvData[grvNumber];
      const lines = grv?.Lines;
      for (const line of lines) {
        grvLines.push({
          grDocEntry: grvNumber,
          ...line,
        });
      }
    }
    const mergedGrvLineItems = grvLines.map((incomingItem) => {
      const existingItem = localDocument?.Document?.gr_line_items?.find(
        (item) =>
          item.grDocEntry === incomingItem.grDocEntry &&
          item.LineNum === incomingItem.LineNum,
      );
      if (existingItem) {
        return {
          ...existingItem,
          ...incomingItem,
          ReceivedQuantity:
            existingItem.ReceivedQuantity === undefined
              ? 0
              : existingItem.ReceivedQuantity,
          ReceivedLineTotal:
            existingItem.ReceivedLineTotal === undefined
              ? 0
              : existingItem.ReceivedLineTotal,
        };
      }

      return {
        ...incomingItem,
        ReceivedQuantity: 0,
        ReceivedLineTotal: 0,
      };
    });
    setGrvLines(mergedGrvLineItems);

    const grvReceivedTotal = mergedGrvLineItems.reduce(
      (acc, line) =>
        acc + parseFloat(line.LineStatus === 'O' ? line.ReceivedLineTotal : 0),
      0,
    );
    setGrvReceivedTotal(grvReceivedTotal);

    if (docType === 'I') {
      setActiveTab('grvLines');
    } else if (docType !== 'I') {
      setActiveTab('poLines');
    } else {
      setActiveTab('invoiceLines');
    }
  }, [purchaseOrder]);

  const updateApproverDetails = useCallback(
    (approverEmail) => {
      const newApprover = approvers.find(
        (approver) => approver.value === approverEmail,
      );

      modals.openConfirmModal({
        title: 'Confirmation',
        children: (
          <Text size="sm">
            {`This action will set "${newApprover.name}" as an approver for this invoice. We will deliver the approval email to "${approverEmail}" upon your confirmation.`}
          </Text>
        ),
        labels: { confirm: 'Confirm', cancel: 'Cancel' },
        onCancel: () => {
          return false;
        },
        onConfirm: async () => {
          setApproverUpdationInProgress(true);

          const invoiceId = document._id;
          const approverEmail = newApprover.value;
          const approverName = newApprover.name;
          const supplierName = document.Document?.supplier?.name || '';
          const poNumber = document.Document?.po_number || '';
          const invoiceAmount = document.Document?.totals?.total || '';
          const invoiceNumber = document.Document?.invoice_number || '';
          const invoiceFileName = document.file_name || '';

          const payload = {
            invoiceId,
            approverEmail,
            approverName,
            supplierName,
            poNumber,
            invoiceAmount,
            invoiceNumber,
            invoiceFileName,
          };

          try {
            await InvoiceApproverAdapter.assignInvoiceApprover(payload);
          } catch (error) {
            console.log(
              '🚀 ~ file: DataGrid2.jsx:181 ~ onConfirm: ~ error:',
              error,
            );
            // TODO: Notify user about the action failure...
          } finally {
            setApproverUpdationInProgress(false);
            refetchDocument();
            // navigate('/');
          }
        },
      });
    },
    [approvers, document],
  );

  const onExpensesEditRemoveRow = useCallback(() => {
    if (lastClickedRow !== null) {
      let data = [...expenseRows];
      data.splice(lastClickedRow, 1);
      setExpenseRows(data);
      handleInputChange('expenseRows', data);
    }
  }, [expenseRows, lastClickedRow]);

  const onExpensesEditAddRow = useCallback(() => {
    let data = [...expenseRows];
    let location = data.length;
    let newRow = {
      LineNum: '',
      Code: '',
      Description: '',
      Quantity: '',
      UnitPrice: '',
      LineTotal: '',
      LineStatus: '',
      LineFormatCode: '',
    };
    newRow.id = location;
    data[location] = newRow;
    const lastItem = data.pop();
    data.splice(lastClickedRow, 0, lastItem);
    for (let index = 0; index < data.length; index++) {
      data[index].id = index;
    }
    setExpenseRows(data);
    handleInputChange('expenseRowsNew', data);
  }, [expenseRows, lastClickedRow]);

  useEffect(() => {
    if (lastClickedRow !== null) {
      // onExpensesEditAddRow(lastClickedRow);
    }
  }, [lastClickedRow, onExpensesEditAddRow]);

  const onExpensesEditComplete = useCallback(
    ({ value, columnId, rowId }) => {
      const data = [...expenseRows];
      // let dataList = [...expenseRowList];
      if (rowId === expenseRows.length) {
        rowId = rowId - 1;
      }
      console.log(data[rowId][columnId]);
      data[rowId][columnId] = value;

      setExpenseRows(data);
      handleInputChange('expenseRows', data);
    },
    [expenseRows],
  );

  const handlePurchaseOrderRowUpdate = async (updatedRow, poLines) => {
    console.log('PO Lines before update', poLines);
    const poLineItems = poLines.map((line) => {
      return line.LineNumber === updatedRow.LineNumber &&
        line.PONumber === updatedRow.PONumber
        ? { ...line, ...updatedRow }
        : line;
    });
    var receivedTotal = 0;
    for (let i = 0; i < poLineItems.length; i++) {
      receivedTotal += parseFloat(
        poLineItems[i]?.ReceivedTotal ? poLineItems[i].ReceivedTotal : '0',
      );
    }
    setProcessedTotal(receivedTotal);
    console.log('Processed Total:', processedTotal);
    setLocalDocument({
      ...localDocument,
      Document: { ...localDocument.Document, po_line_items: poLineItems },
    });
    const updatedDoc = {
      ...localDocument,
      Document: { ...localDocument.Document, po_line_items: poLineItems },
    };
    console.log('Current Item after checking the item....', updatedDoc);
    let body = {
      transactionId: updatedDoc._id,
      templateObject: updatedDoc,
    };
    await TransactionAdapter.updateTransaction(body);
  };

  const handleGRVLineUpdate = async (updatedRow, grvLines) => {
    console.log('Updated Row:', updatedRow);
    const grvLineItems = grvLines.map((line) => {
      return line.LineNum === updatedRow.LineNum &&
        line.grDocEntry === updatedRow.grDocEntry
        ? { ...line, ...updatedRow }
        : line;
    });

    const grvReceivedTotal = grvLineItems.reduce(
      (acc, line) => acc + parseFloat(line.ReceivedLineTotal),
      0,
    );

    setGrvReceivedTotal(grvReceivedTotal);

    /*toast.info(
      `Updating GRV/LineNum: ${updatedRow.grDocEntry}/${updatedRow.LineNum}...`,
    );*/

    setLocalDocument({
      ...localDocument,
      Document: { ...localDocument.Document, gr_line_items: grvLineItems },
    });

    const updatedDoc = {
      ...localDocument,
      Document: { ...localDocument.Document, gr_line_items: grvLineItems },
    };
    let body = {
      transactionId: updatedDoc._id,
      templateObject: updatedDoc,
    };

    await TransactionAdapter.updateTransaction(body);
    /*toast.success(
      `Successfully updated GRV/LineNum: ${updatedRow.grDocEntry}/${updatedRow.LineNum}`,
    );*/

    // To avoid showing stale data in the UI.
    //invalidateDocumentCache();
  };

  const handleInputChange = (key, value) => {
    setTemplateObject({ ...templateObject, [key]: value });
  };

  const handleBotInputChange = (key, value) => {
    setBotObject({ ...botObject, [key]: value });
  };

  const submitOpenItem = async (item) => {
    toast.info('Busy processing case');
    let tempObject = templateObject;
    tempObject.botObject = botObject;
    tempObject.botObject.Expenses = botExpenseRows;
    let expenses = [];
    Object.keys(botObject).forEach((key) => {
      if (typeof botObject[key] === 'string' && botObject[key] === '') {
        toast.error(`Please enter ${key}`);
        return false;
      }
    });
    for (let index = 0; index < expenseRows.length; index++) {
      let element = expenseRows[index];
      delete element.id;
      const arrayFromObject = Object.values(element);
      for (let index = 0; index < arrayFromObject.length; index++) {
        const arrayElemnt = arrayFromObject[index];
        if (arrayElemnt !== '') {
          expenses.push(arrayFromObject);
          break;
        }
      }
    }

    tempObject.expenseRows = expenses;
    tempObject.botObject.lineItemData = expenseRows;

    const updatedDoc = getUpdatedDocObject(item, tempObject);
    updatedDoc.state = docStatusMap.zeroQ;

    let body = {
      transactionId: item._id,
      templateObject: updatedDoc,
    };

    await TransactionAdapter.updateTransaction(body);

    handleDocumentRemovedFromQueue(item._id);

    toast.success('Case successfully processed');

    // navigate('/');
    refetchDocument();
  };

  const pushBackItem = async (item) => {
    toast.info('Pushing back to the Manual Queue');

    let body = {
      transactionId: item._id,
      templateObject: {
        state: docStatusMap.manualQ,
      },
    };

    await TransactionAdapter.updateTransaction(body);

    handleDocumentRemovedFromQueue(item._id);

    toast.success('Pushed back succesfully');

    // navigate('/');
    refetchDocument();
  };

  const sqaushOpenItem = async (item) => {
    toast.info('Busy squashing case');

    let body = {
      transactionId: item._id,
      templateObject: {
        state: docStatusMap.squashQ,
      },
    };

    await TransactionAdapter.updateTransaction(body);

    handleDocumentRemovedFromQueue(item._id);

    toast.success('Case squashed succesfully');

    // navigate('/');
    refetchDocument();
  };

  const pushToBatchQ = async (invoiceId) => {
    let POTotal = purchaseOrder?.PurchaseOrders?.RemainingBalance;
    let receivedTotal = 0;

    if (purchaseOrder?.PurchaseOrders?.DocType === 'I') {
      for (let i = 0; i < localDocument.Document.gr_line_items.length; i++) {
        const grLine = localDocument.Document.gr_line_items[i];
        if (grLine.LineStatus == 'O') {
          console.log('Checking GRV total received:::', receivedTotal, grLine);
          receivedTotal += parseFloat(
            grLine?.ReceivedLineTotal ? grLine.ReceivedLineTotal : '0',
          );
        }
      }
    } else {
      for (let i = 0; i < localDocument.Document.po_line_items.length; i++) {
        const poLine = localDocument.Document.po_line_items[i];
        if (poLine.LineStatus == 'O') {
          console.log(
            'Checking Service total received:::',
            receivedTotal,
            poLine,
          );
          receivedTotal += parseFloat(
            poLine?.ReceivedTotal ? poLine.ReceivedTotal : '0',
          );
        }
      }
    }

    if (receivedTotal == 0) {
      modals.openConfirmModal({
        title: 'Confirmation',
        children: (
          <Text size="sm">
            {`All received total are zero. Please input received total on Purchase Order lines`}
          </Text>
        ),
        labels: { cancel: 'Cancel' },
        onCancel: () => {
          return false;
        },
      });
      return;
    }
    if (parseFloat(receivedTotal) > parseFloat(POTotal) + 0.5) {
      modals.openConfirmModal({
        title: 'Confirmation',
        children: (
          <Text size="sm">
            {`The sum of the received totals can not be greater than the remaining balance on the purchase order`}
          </Text>
        ),
        labels: { cancel: 'Cancel' },
        onCancel: () => {
          return false;
        },
      });
      return;
    }

    toast.info('Pushing to the Batch Queue.');

    const updatedDoc = {};
    updatedDoc.state = docStatusMap.batchQ;

    let body = {
      transactionId: invoiceId,
      templateObject: updatedDoc,
    };

    await TransactionAdapter.updateTransaction(body);

    handleDocumentRemovedFromQueue(invoiceId);
  };

  const pushCurrentInvoiceToSapQ = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    pushToSapQ([invoiceId]);
  };

  const pushToSapQ = async (invoiceIds) => {
    if (!Array.isArray(invoiceIds) || invoiceIds.length === 0) {
      return;
    }

    toast.info('Started pushing to SAP Queue', {
      type: toast.TYPE.INFO,
      autoClose: 3000,
    });

    try {
      await TransactionAdapter.pushToSapQ(invoiceIds);

      invoiceIds.forEach((invoiceId) => {
        handleDocumentRemovedFromQueue(invoiceId);
      });

      toast.info('Successfully pushed to SAP Queue', {
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });
    } catch (e) {
      console.error('Error in pushing to SAP Queue: ', e);

      toast.info('Something went wrong while pushing to SAP Queue', {
        type: toast.TYPE.ERROR,
        autoClose: 3000,
      });
    } finally {
      navigate('/');
    }
  };

  let itemForm = useForm({
    initialValues: {},
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  });

  /**
   * Transforms the object structure to the current one.
   * This transformation aims to retrofit the implementation and
   * should be removed/improved later.
   */
  function getBotObject(doc) {
    const invoiceReceivedDate = new Date(doc.date_added)
      .toISOString()
      .split('T')[0];
    const {
      document_type,
      account_number,
      invoice_number,
      po_number,
      invoice_date,
    } = doc.Document;
    const { name: supplierName, vat_number } = doc.Document.supplier;
    const { total } = doc.Document.totals;
    const lineItems = (doc.Document.line_items || []).map((lineItem, index) => {
      return {
        LineNum: index,
        Code: lineItem.code,
        Description: lineItem.description,
        Quantity: lineItem.quantity,
        UnitPrice: lineItem.unit_rate,
        LineTotal: lineItem.total,
        Discount: lineItem.discount || 0,
        LineStatus: '',
        LineFormatCode: '',
      };
    });

    return {
      Document_Type: document_type || '',
      Account_Number: account_number || '',
      Invoice_Number: invoice_number || '',
      PO_Number: po_number || '',
      Supplier: supplierName || '',
      Invoice_Date: invoice_date || '',
      Invoice_Received_Date: invoiceReceivedDate || '',
      Total_Amount: (total || total === 0 ? total : '').toString(),
      Vat_Number: vat_number || '',
      lineItemData: lineItems,
      Expenses: [],
    };
  }

  /**
   * Transforms the object structure back to the new "document" structure.
   * This transformation aims to retrofit the implementation and
   * should be removed/improved later.
   */
  function getUpdatedDocObject(doc, bot) {
    const updatedDoc = { ...doc };

    updatedDoc.Document.document_type = bot.botObject.Document_Type;
    updatedDoc.Document.account_number = bot.botObject.Account_Number;
    updatedDoc.Document.invoice_number = bot.botObject.Invoice_Number;
    updatedDoc.Document.po_number = bot.botObject.PO_Number;
    updatedDoc.Document.supplier.name = bot.botObject.Supplier;
    updatedDoc.Document.supplier.vat_number = bot.botObject.Vat_Number;
    updatedDoc.Document.invoice_date = bot.botObject.Invoice_Date;
    updatedDoc.date_added = bot.botObject.Invoice_Received_Date;
    updatedDoc.Document.totals.total = bot.botObject.Total_Amount;
    updatedDoc.Document.line_items = (bot.botObject.lineItemData || []).map(
      (lineItem) => {
        return {
          code: lineItem.Code,
          description: lineItem.Description,
          quantity: lineItem.Quantity,
          unit_rate: lineItem.UnitPrice,
          total: lineItem.LineTotal,
        };
      },
    );

    return updatedDoc;
  }

  /**
   * Sets the `localDocument` state to the selected document version.
   * If user clears the document version history dropdown then
   * binds the `localDocument` to the original `document`.
   *
   * @param selectedDocVersion - selected document object. It will be "" when user
   * clears the Document Version History dropdown to go back to the current version.
   */
  const handleDocHistorySelection = (selectedDocVersion) => {
    const selectedDoc = selectedDocVersion || document;
    let rows = [];

    setLocalDocument(selectedDoc);
    const botObject = getBotObject(selectedDoc);
    setBotObject(botObject);

    const templateObj = {
      matchedTemplateId: '',
      extraFields: {},
      botObject: { ...botObject },
      expenseRows: [],
      expenseRowsNew: [],
    };
    itemForm.setValues(templateObj);

    for (
      let index = 0;
      index < templateObj.botObject.lineItemData.length;
      index++
    ) {
      const element = templateObj.botObject.lineItemData[index];
      let rowItem = element;
      rowItem.id = index;
      rows.push(rowItem);
    }

    setExpenseRows(rows);
    setTemplateObject(templateObj);
  };

  const appendPurchaseOrderTotal = (tabTitle = '') => {
    const total = formatCurrency(purchaseOrder?.PurchaseOrders?.DocTotal);
    return total ? `${tabTitle} - ${total}` : tabTitle;
  };

  const handleGoBack = () => {
    // Check if the user was redirected to this page from the "/login" route.
    // This occurs when a user directly accesses the "document/<doc-id>" URL
    // without being logged in. In such cases, the user is first redirected
    // to "/login" and, upon successful login, returned to their intended path.
    const isRedirectedFromLogin = location.state?.redirectFrom === '/login';

    if (isRedirectedFromLogin) {
      // If redirected from login, check if there was a queue parameter in the original URL
      const urlParams = new URLSearchParams(window.location.search);
      const queueParam = urlParams.get('q');

      if (queueParam) {
        // Navigate back to the documents page with the queue parameter preserved
        navigate(`/?q=${queueParam}`, { replace: true });
      } else {
        // No queue parameter, go to default dashboard
        navigate('/', { replace: true });
      }
    } else {
      // Try to go back in history if possible, otherwise fall back to home with queue context
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Extract queue parameter from current URL to preserve context
        const urlParams = new URLSearchParams(window.location.search);
        const queueParam = urlParams.get('q');

        if (queueParam) {
          navigate(`/?q=${queueParam}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    }
  };

  const FooterSection = () => {
    return (
      <Box
        sx={(theme) => ({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '32px',
          backgroundColor: '#f0f0f0',
          borderTop: `1px solid ${theme.colors.gray[3]}`,
          padding: theme.spacing.xs,
          paddingRight: theme.spacing.md,
          paddingLeft: theme.spacing.md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 100,
        })}>
        <Group spacing="xs">
          <Text size="lg" weight={600}>
            PO Total (Incl):
          </Text>
          <Text size="lg" weight={600}>
            R{' '}
            {Number(purchaseOrder?.PurchaseOrders?.DocTotal || 0)
              .toFixed(2)
              .toLocaleString('en-ZA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
          </Text>
        </Group>
        <Group spacing="xs">
          <Text
            size="lg"
            weight={600}
            color={
              (grvReceivedTotal || processedTotal || 0) >
                (purchaseOrder?.PurchaseOrders?.RemainingBalance || 0) ||
              (grvReceivedTotal || processedTotal || 0) > invoiceTotal
                ? 'red'
                : 'inherit'
            }>
            Processing (Excl):
          </Text>
          <Text
            size="lg"
            weight={600}
            color={
              (grvReceivedTotal || processedTotal || 0) >
                (purchaseOrder?.PurchaseOrders?.RemainingBalance || 0) ||
              (grvReceivedTotal || processedTotal || 0) > invoiceTotal
                ? 'red'
                : 'inherit'
            }>
            R{' '}
            {(grvReceivedTotal || processedTotal || 0).toLocaleString('en-ZA', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Group>
        <Group spacing="xs">
          <Text size="lg" weight={600}>
            Max Value Allowed (Excl):
          </Text>
          <Text size="lg" weight={600}>
            R{' '}
            {(
              purchaseOrder?.PurchaseOrders?.RemainingBalance || 0
            ).toLocaleString('en-ZA', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Group>
      </Box>
    );
  };

  const handleAddNote = async (values) => {
    try {
      // Optimistic update - Add note to local state first
      setLocalDocument({
        ...localDocument,
        notes: [
          ...(localDocument?.notes || []),
          {
            content: values.content,
            created_by: { userName: localStorage.getItem('userName') },
            created_at: new Date(),
          },
        ],
      });

      await TransactionAdapter.addNote(id, values.content);

      // Clear the form after successful submission
      noteForm.reset();

      refetchDocument();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');

      // Revert the optimistic update
      setLocalDocument(document);
    }
  };

  const NoteCard = (note) => (
    <Card key={note._id} shadow="xs" withBorder>
      <Group position="apart" mb={4}>
        <Text size="sm" weight={500}>
          {note.created_by.userName}
        </Text>
      </Group>
      <Text size="sm" mb={8}>
        {note.content}
      </Text>
      <Text size="xs" color="dimmed">
        {new Date(note.created_at)
          .toISOString()
          .replace('T', ' ')
          .substring(0, 19) || ''}
      </Text>
    </Card>
  );

  if (isLoading || !localDocument) {
    return (
      <LoadingScreen
        message="Loading document..."
        description="Juice is loading document details. Please wait..."
      />
    );
  }

  return (
    <>
      {/* Notes Drawer */}
      <Drawer.Root
        opened={isNotesDrawerOpen}
        onClose={closeNotesDrawer}
        closeOnClickOutside={false}
        title="Invoice Notes"
        size="md"
        padding="md"
        position="right">
        <Drawer.Overlay />
        <Drawer.Content>
          <Drawer.Header>
            <Title order={4}>Invoice Notes</Title>
            <Drawer.CloseButton />
          </Drawer.Header>
          <Drawer.Body>
            <Stack spacing="md">
              {/* Add Note Form */}
              <Card shadow="xs" bg="gray.1" py="lg">
                <form onSubmit={noteForm.onSubmit(handleAddNote)}>
                  <Stack spacing="sm">
                    <Textarea
                      minRows={3}
                      maxRows={3}
                      label=""
                      placeholder="Write your note here..."
                      data-autofocus
                      {...noteForm.getInputProps('content')}
                    />
                    <Group position="right">
                      <Button
                        variant="filled"
                        type="submit"
                        color="cyan"
                        loading={noteForm.isSubmitting}>
                        ADD NOTE
                      </Button>
                    </Group>
                  </Stack>
                </form>
              </Card>

              {/* Notes List */}
              <ScrollArea
                h={`calc(100vh - 270px)`}
                type="auto"
                offsetScrollbars>
                <Stack spacing="sm">
                  {localDocument.notes?.length > 0 ? (
                    localDocument.notes
                      .sort(
                        (a, b) =>
                          new Date(b.created_at) - new Date(a.created_at),
                      )
                      .map(NoteCard)
                  ) : (
                    <Text color="dimmed" align="center" py="xl">
                      No notes added yet
                    </Text>
                  )}
                </Stack>
              </ScrollArea>
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>

      <SimpleGrid cols={isViewSwitched === true ? 1 : 2}>
        <Card
          style={{
            height: 'calc(100vh - 40px)',
            overflow: 'auto',
            scrollbarWidth: 'none',
            scrollbarColor: 'transparent transparent',
          }}>
          <Group position="apart" mb="xs" noWrap>
            <Button
              variant="default"
              type="button"
              size="sm"
              ref={backButtonRef}
              leftIcon={<IconArrowLeft size={14}></IconArrowLeft>}
              onClick={handleGoBack}>
              GO BACK
            </Button>

            <Group position="center" sx={{ flex: 1 }}>
              <Button
                ref={prevButtonRef}
                type="button"
                variant="default"
                size="sm"
                disabled={!navigationTargets.prev}
                onClick={() =>
                  navigate(`/documents/${navigationTargets.prev._id}`, {
                    replace: true,
                  })
                }
                leftIcon={
                  <IconCaretLeftFilled size={18}></IconCaretLeftFilled>
                }>
                PREV
              </Button>
              <Button
                ref={nextButtonRef}
                type="button"
                variant="default"
                size="sm"
                disabled={!navigationTargets.next}
                onClick={() =>
                  navigate(`/documents/${navigationTargets.next._id}`, {
                    replace: true,
                  })
                }
                rightIcon={
                  <IconCaretRightFilled size={18}></IconCaretRightFilled>
                }>
                NEXT
              </Button>
            </Group>

            <Group spacing="xs" noWrap>
              <Button
                color="orange"
                type="button"
                size="sm"
                rightIcon={<IconArrowRight size={18}></IconArrowRight>}
                leftIcon={<IconMessages size={18}></IconMessages>}
                onClick={openNotesDrawer}>
                NOTES
              </Button>
              {isViewSwitched === true ? (
                <Button
                  type="button"
                  variant="gradient"
                  gradient={{ from: 'yellow', to: 'yellow', deg: 60 }}
                  onClick={() => switchView(null)}>
                  SWITCH VIEW
                </Button>
              ) : null}
            </Group>
          </Group>

          <Group position="apart">
            <Stack spacing="none">
              <Title order={3}>Invoice Details </Title>
              <Text color="gray">{docStatusNameMap[document.state]}</Text>
            </Stack>

            {hideDocHistoryFrom.includes(document.state) ? (
              <></>
            ) : (
              <DocHistoryDropdown
                sourceId={document._id}
                onSelect={handleDocHistorySelection}></DocHistoryDropdown>
            )}
          </Group>

          <Divider my="xs" />

          {/* Error Message Alert */}
          {localDocument.message && (
            <Alert
              title="Info"
              variant="light"
              color="blue"
              icon={<IconInfoCircle />}>
              {localDocument.message}
            </Alert>
          )}

          {/* Service Invoice Approver Alert - No Approver Selected */}
          {document.state === docStatusMap.serviceInvoiceQ &&
            !document.approver &&
            !approverUpdationInProgress && (
              <Alert
                title="Approval Required"
                variant="light"
                color="blue"
                my="md"
                icon={<IconInfoCircle />}>
                This service invoice needs to be reviewed by an authorized
                approver before it can proceed. Please select an approver from
                the list to ensure timely processing.
                <Group position="left">
                  <Tooltip
                    withArrow
                    label={
                      'This will mark this invoice as approved by you and move it to the next stage.'
                    }>
                    <Button
                      type="button"
                      color="red"
                      variant="outline"
                      leftIcon={<IconCircleCheck />}
                      onClick={() => pushToBatchQ(document._id)}>
                      SELF APPROVE
                    </Button>
                  </Tooltip>
                </Group>
              </Alert>
            )}

          {/* Service Invoice Approver Alert - Approver has approved. */}
          {[
            docStatusMap.batchQ,
            docStatusMap.sapWaitingQ,
            docStatusMap.completeQ,
          ].includes(document.state) &&
            document.approver &&
            !approverUpdationInProgress &&
            document?.approver?.has_approved && (
              <Alert
                title="Approved Invoice"
                variant="light"
                color="green"
                my="md"
                icon={<IconInfoCircle />}>
                <Text>
                  {`This invoice has been approved by ${document.approver.name} (${document.approver.email}).`}
                </Text>
              </Alert>
            )}

          {/* Service Invoice Approver Alert - Approver has rejected. */}
          {[docStatusMap.rejectedQ].includes(document.state) &&
            document.approver &&
            !approverUpdationInProgress &&
            document?.approver?.remarks &&
            !document?.approver?.has_approved && (
              <Alert
                title="Rejection Reason"
                variant="light"
                color="red"
                my="md"
                icon={<IconInfoCircle />}>
                <Text>{`${document.approver.remarks}`}</Text>
                <Group position="left">
                  <Tooltip
                    withArrow
                    label={
                      'This will mark this invoice as approved by you and move it to the next stage.'
                    }>
                    <Button
                      type="button"
                      color="red"
                      variant="outline"
                      leftIcon={<IconCircleCheck />}
                      onClick={() => pushToBatchQ(document._id)}>
                      SELF APPROVE
                    </Button>
                  </Tooltip>
                </Group>
              </Alert>
            )}

          {/* Service Invoice Approver Alert - Has approver but not approved. */}
          {document.state === docStatusMap.serviceInvoiceQ &&
            document.approver &&
            !document.approver?.has_approved &&
            !approverUpdationInProgress && (
              <Alert
                title="Awaiting Approval"
                variant="light"
                color="orange"
                my="md">
                <p>
                  <Text>
                    {`This invoice requires an approval from ${document.approver.name} (${document.approver.email}).`}
                  </Text>
                </p>

                <Tooltip
                  withArrow
                  label={`This will resend invoice approval email to "${document.approver.email}".`}>
                  <Button
                    type="button"
                    color="orange"
                    variant="filled"
                    leftIcon={<IconMailFast />}
                    onClick={() =>
                      updateApproverDetails(document?.approver?.email)
                    }>
                    RESEND APPROVAL
                  </Button>
                </Tooltip>
                <Tooltip
                  withArrow
                  label={
                    'This will mark this invoice as approved by you and move it to the next stage.'
                  }>
                  <Button
                    type="button"
                    color="orange"
                    variant="outline"
                    leftIcon={<IconCircleCheck />}
                    onClick={() => pushToBatchQ(document._id)}>
                    SELF APPROVE
                  </Button>
                </Tooltip>
              </Alert>
            )}

          {/* Service Invoice Approver Alert - Approver updation request in progress. */}
          {(document.state === docStatusMap.serviceInvoiceQ ||
            document.state === docStatusMap.rejectedQ) &&
            approverUpdationInProgress && (
              <Alert
                title="Setting Approver"
                variant="light"
                color="blue"
                my="md"
                icon={<Loader color="blue" size="xs" />}>
                Saving the invoice approver details.
              </Alert>
            )}

          <Card.Section inheritPadding py="sm">
            <form onSubmit={itemForm.onSubmit((values) => console.log(values))}>
              <SimpleGrid
                verticalSpacing="xs"
                cols={isViewSwitched === true ? 4 : 3}>
                {/* Invoice Details Fields */}
                {Object.keys(botObject).map(
                  (key) =>
                    typeof botObject[key] === 'string' &&
                    key !== 'approver' && (
                      <TextInput
                        key={key}
                        id={key + '_input'}
                        label={key.toUpperCase().replaceAll('_', ' ')}
                        description={formFieldDescriptions[key]}
                        inputWrapperOrder={[
                          'label',
                          'input',
                          'description',
                          'error',
                        ]}
                        disabled={
                          editableViews.includes(document.state) ? false : true
                        }
                        value={botObject[key] || ''}
                        error={
                          !botObject[key]
                            ? 'Please set ' +
                              key.toUpperCase().replaceAll('_', ' ') +
                              '.'
                            : ''
                        }
                        onChange={(e) =>
                          handleBotInputChange(key, e.target.value)
                        }
                        withAsterisk
                      />
                    ),
                )}

                {/* Invoice Approver Dropdown */}
                {(document.state === docStatusMap.serviceInvoiceQ ||
                  document.state === docStatusMap.rejectedQ) && (
                  <Select
                    id="approver_select"
                    label="APPROVER"
                    placeholder="Select an approver"
                    data={approvers.filter((approver) => {
                      // Exclude approvers whose Approval Limit < Invoice Total.
                      return (
                        approver.limit >= Number(botObject['Total_Amount'])
                      );
                    })}
                    itemComponent={SelectApproverItem}
                    value={document.approver?.email || ''}
                    onChange={updateApproverDetails}
                    searchable
                    nothingFound="No match found..."
                    withAsterisk
                  />
                )}
              </SimpleGrid>
            </form>
          </Card.Section>

          {document.notes?.length > 0 && (
            <Alert title="Latest Note" variant="light" color="yellow" mb="md">
              {document.notes
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 1)
                .map((note) => note.content)}
            </Alert>
          )}

          {/* TODO: This should be extracted to a separate component. */}
          <Tabs
            color="cyan"
            variant="default"
            value={activeTab}
            onTabChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="invoiceLines">Invoice Lines</Tabs.Tab>
              {purchaseOrder?.PurchaseOrders?.DocType !== 'I' && (
                <Tabs.Tab value="poLines">Purchase Order</Tabs.Tab>
              )}
              {purchaseOrder?.PurchaseOrders?.DocType === 'I' && (
                <Tabs.Tab value="grvLines">Goods Receipts</Tabs.Tab>
              )}
            </Tabs.List>
            <Tabs.Panel value="invoiceLines" pt="xs">
              {/* Add Line / Remove Line Buttons */}
              {editableViews.includes(document.state) && (
                <Group position="right" justify="space-between" mb="xs">
                  <Button
                    variant="filled"
                    size="xs"
                    color="green.5"
                    leftIcon={<IconPlus size={18} />}
                    onClick={() => onExpensesEditAddRow(0)}
                    id={`button-AddLine`}>
                    ADD LINE
                  </Button>
                  <Button
                    variant="filled"
                    size="xs"
                    color="red.5"
                    leftIcon={<IconMinus size={18} />}
                    onClick={() => onExpensesEditRemoveRow(0)}>
                    REMOVE LINE
                  </Button>
                </Group>
              )}

              {/* Invoice Line Items Grid */}
              <ReactDataGrid
                idProperty="id"
                style={{ minHeight: 300 }}
                onEditComplete={onExpensesEditComplete}
                editable={editableViews.includes(document.state) ? true : false}
                columns={expenseColumn}
                dataSource={expenseRows}
                onRowClick={onRowClick}
              />
            </Tabs.Panel>
            {purchaseOrder?.PurchaseOrders?.DocType !== 'I' && (
              <Tabs.Panel value="poLines" pt="xs">
                <PurchaseOrderLines
                  style={gridStyle}
                  poLines={poLines}
                  editable={
                    editableViews.includes(document.state) ? true : false
                  }
                  isLoading={purchaseOrderLoading}
                  error={purchaseOrderError}
                  onRowUpdate={handlePurchaseOrderRowUpdate}
                />
              </Tabs.Panel>
            )}
            {purchaseOrder?.PurchaseOrders?.DocType === 'I' && (
              <Tabs.Panel value="grvLines" pt="xs">
                <GRVLines
                  style={gridStyle}
                  grvLines={grvLines}
                  isLoading={purchaseOrderLoading}
                  editable={
                    editableViews.includes(document.state) ? true : false
                  }
                  onRowUpdate={handleGRVLineUpdate}
                />
              </Tabs.Panel>
            )}
          </Tabs>

          <Divider my="sm" />

          <Grid>
            <Grid.Col span={9}>
              <Group position="left">
                {pushBackViews.includes(document.state) && (
                  <Tooltip
                    withArrow
                    label="Moves this transaction back to Manual Queue.">
                    <Button
                      variant="filled"
                      color="orange.6"
                      leftIcon={<IconArrowBackUp size={18} />}
                      type="button"
                      onClick={() => pushBackItem(document)}>
                      PUSH BACK
                    </Button>
                  </Tooltip>
                )}
                {squashViews.includes(document.state) ? (
                  <Tooltip
                    withArrow
                    label="Permanently discards and moves this record to the Squashed Queue.">
                    <Button
                      variant="filled"
                      color="red.6"
                      leftIcon={<IconTrash size={18} />}
                      type="button"
                      onClick={() => sqaushOpenItem(document)}>
                      SQUASH
                    </Button>
                  </Tooltip>
                ) : null}
                {/* Move to Batch Button */}
                {document.state === docStatusMap.grvInvoiceQ && (
                  <Tooltip
                    withArrow
                    label="Moves this transaction to Batch Queue.">
                    <Button
                      variant="filled"
                      color="green.7"
                      type="submit"
                      leftIcon={<IconUpload size={18} />}
                      onClick={() => pushToBatchQ(document._id)}>
                      MOVE TO BATCH
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Grid.Col>
            <Grid.Col span={3}>
              <Group position="right">
                {/* Update & Cancel Buttons */}
                {editableViews.includes(document.state) && (
                  <Tooltip
                    withArrow
                    label="Moves this record to Zero Queue to validate your changes.">
                    <Button
                      variant="filled"
                      color="green.7"
                      type="submit"
                      leftIcon={<IconCloudUpload size={18} />}
                      onClick={() => submitOpenItem(localDocument)}>
                      UPDATE
                    </Button>
                  </Tooltip>
                )}

                {/* Process Transaction Button */}
                {(document.state === docStatusMap.batchQ ||
                  document.state === docStatusMap.sapQueryQ) && (
                  <Tooltip
                    withArrow
                    label="Moves this transaction to SAP Waiting Queue.">
                    <Button
                      variant="filled"
                      color="green.7"
                      type="submit"
                      leftIcon={<IconUpload size={18} />}
                      onClick={() => pushCurrentInvoiceToSapQ(document._id)}>
                      PROCESS NOW
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Grid.Col>
          </Grid>
        </Card>
        <>
          {isViewSwitched === false && documentKey ? (
            <PDFViewerNative
              fileUrl={docSignedUrl}
              loading={docSignedUrlLoading}
              error={docSignedUrlError}
            />
          ) : null}
        </>
      </SimpleGrid>
      <FooterSection />
    </>
  );
}

export default DocumentDetails;
